/* eslint-disable max-len */
import React, { useMemo } from "react";
import AvatarCustom from "../AvatarCustom/AvatarCustom";
import { calculateDaysUntilExpiry, fromISOToFormatDate, fromISOToFormatDatePlusDays } from "../../utils";
import { billCycle } from "../../shared/containers/WellnessPlanPaymentContainer/types";
import { MODALS } from "../../config/params";
import { useModal } from "../../context/ModalContext";
import { userActivityLogger } from "../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";
import { PaymentMethodProvider } from "../../services/renewal-preference/renewal-preference.interface";
import { useScratchPayRenewal } from "../../hooks/useScratchPayRenewal";
import { Button, CircularProgress } from "@mui/material";

interface IUpcomingPlanCard {
  petName: string;
  petImage?: string;
  subscriptionFee: string;
  billCycle: billCycle;
  planName: string;
  planStartDate: string;
  currentSubscriptionId: string;
  petId: string;
  paymentMethodProvider?: PaymentMethodProvider | null;
  planType?: string;
  scratchpayPlanId?: number | null;
}

const UpcomingPlanCard = ({
  petName,
  petImage,
  planName,
  planStartDate,
  billCycle,
  subscriptionFee,
  currentSubscriptionId,
  paymentMethodProvider,
  petId,
  planType,
  scratchpayPlanId,
}: IUpcomingPlanCard) => {
  const { openModal } = useModal();

  const isScratchPay = paymentMethodProvider === PaymentMethodProvider.SCRATCH_PAY;

  const { isExpired } = useMemo(
    () => calculateDaysUntilExpiry(planStartDate),
    [planStartDate],
  );

  const hasScratchPayData = isScratchPay && planType && scratchpayPlanId;

  const scratchPayRenewal = useScratchPayRenewal({
    petId,
    petName,
    planName,
    planType: planType || undefined,
    subscriptionId: currentSubscriptionId,
    scratchpayPlanId: scratchpayPlanId || undefined,
  });

  const shouldShowApplyButton = hasScratchPayData && isExpired;
  const isExpiredScratchPay = isScratchPay && isExpired;

  const formatPlanStartDate = (date: string) => {
    if (!date) {
      return "N/A";
    }

    // The renewal plan starts the day after the current plan ends.
    return fromISOToFormatDatePlusDays(date, 1);
  };

  const handleCancelRenewalClick = () => {
    userActivityLogger(MIXPANEL_EVENTS.USER_CLICK_CANCEL_RENEWAL, {
      pet_name: petName,
      wellness_plan_name: planName,
      referred_from: "plan_and_billing",
    });

    openModal(MODALS.CANCEL_UPCOMING_PLAN_RENEWAL, "plan_and_billing", {
      subscriptionId: currentSubscriptionId,
      petId: petId,
    });
  };

  return (
    <div className="bg-white rounded-xl p-4 flex flex-col gap-4 shadow-lg">
      <div className="flex gap-3">
        <div>
          <AvatarCustom
            image={petImage ?? ""}
            width={65}
            height={65}
            name={petName}
            isWellnessMember={true}
            isPlanOverdue={false}
          />
        </div>
        <div className="flex flex-col gap-2 flex-1">
          <div className="text-xl font-semibold">
            {isExpiredScratchPay ? (
              <>Renew <span className="capitalize">{petName}</span>&apos;s TotalCare Coverage</>
            ) : (
              planName
            )}
          </div>
          <div className="flex flex-col gap-1 text-sm">
            <div className="capitalize">{petName}</div>
            {!isScratchPay && (
              <>
                <div className="text-violet-1">
                  {!subscriptionFee || subscriptionFee === "0" ?
                    "N/A" :
                    `$${subscriptionFee} ${billCycle === "perMonth" ? "/month" : "/year"}`}
                </div>
                <div className="text-xs">
                  <span className="font-semibold">Plan Starts</span>
                  {`: ${formatPlanStartDate(planStartDate)}`}
                </div>
              </>
            )}
            {isScratchPay && !isExpired && (
              <div className="text-xs text-gray-1 leading-5">
                Your monthly plan preference is saved. We&apos;ll remind you when it&apos;s time to
                complete enrollment with ScratchPay financing.
              </div>
            )}
            {isExpiredScratchPay && (
              <>
                <div className="text-xs text-gray-1 leading-5">
                  Your plan has expired. Complete your monthly {planName} enrollment now.
                </div>
                {planStartDate && (
                  <div className="text-xs font-semibold">
                    Coverage ended on {fromISOToFormatDate(planStartDate)}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {shouldShowApplyButton && (
        <Button
          variant="contained"
          size="small"
          disabled={scratchPayRenewal.preferenceLoading}
          onClick={scratchPayRenewal.handleApplyNow}
          sx={{
            "bgcolor": "info.light",
            "color": "black.main",
            "&:hover": {
              "bgcolor": "gray.light",
              "color": "black.main",
            },
          }}
        >
          {scratchPayRenewal.preferenceLoading ? <CircularProgress size={20} /> : "Complete Purchase"}
        </Button>
      )}

      {isScratchPay && !isExpired && (
        <div className="flex items-center justify-center gap-1.5 text-xs text-light-black font-semibold">
          <span>powered by</span>
          <img
            src="/assets/icons/scratchpay.png"
            alt="ScratchPay"
            className="h-3.5"
          />
        </div>
      )}

      {!isExpiredScratchPay && (
        <div
          onClick={handleCancelRenewalClick}
          className="border border-gray-1 rounded-[6.25rem] py-2 cursor-pointer
          px-5 flex justify-center text-xs font-semibold hover:bg-gray-3">
          {isScratchPay ? "Cancel Selection" : "Cancel Renewal"}
        </div>
      )}
    </div>
  );
};

export default UpcomingPlanCard;

